import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Container } from '../helpers/Grid';
import { SvgImage, ImageObject } from '../common';
import useOutsideAlerter from '../../hooks/UseOutsideAlerter';
import PageHeaderProps from './type';
import trottle from '../../utilities/events';
import breakpoints from '../../breakpoints';
import './styles.scss';

const navOffsetMobile: number = 80;
const navOffsetTablet: number = 150;
const navOffsetLargeTablet: number = 200;
const navOffsetDesktop: number = 220;

const PageHeader = ({
  title,
  links,
  heroImage,
  link,
}: PageHeaderProps) => {
  const linksContainer = useRef<HTMLUListElement>(null);
  const moreLinksContainer = useRef<HTMLDivElement>(null);
  const [linksTotalWidth, setLinksTotalWidth] = useState<number>(0);
  const [hasResizeListener, setHasResizeListener] = useState<boolean>(false);
  const [cutOffIndex, setCutOffIndex] = useState<number>(links?.length);
  const [windowResized, setWindowResized] = useState<boolean>(false);
  const [navOffset, setNavOffset] = useState<number>(150);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  /**
   * Calculates the overall width of all child nodes within
   * a container element.
   * @param container
   * @param margin
   * @returns Number
   */
  const calculateWidthOfChildren = (container: HTMLUListElement, margin: number) => (
    Object.values(container.childNodes).reduce(
      (total, childElement: any) => total + (childElement.offsetWidth + margin), 0,
    )
  );

  /**
   * Handles the offset cut off of the navigation overflow. The offset
   * is used to cap the amount of visible link in view and determine
   * that we display in the view more menue.
   */
  const handleOverflow = () => {
    const { current } = linksContainer;

    if (current) {
      const containerWidth = current.clientWidth;

      if (linksTotalWidth > containerWidth) {
        setCutOffIndex((cutOffIndex - 1));
      } else if (linksTotalWidth <= (containerWidth - 200)) {
        setCutOffIndex((cutOffIndex + 1));
      }
    }
  };

  /**
   * Change the navOffset based on the current screen-size.
   */
  const handleNavOffset = () => {
    console.log('navOffset', navOffset);
    if (window.innerWidth >= breakpoints.md) {
      setNavOffset(navOffsetTablet);
    } else if (window.innerWidth >= breakpoints.lg) {
      setNavOffset(navOffsetLargeTablet);
    } else if (window.innerWidth >= breakpoints.xl) {
      setNavOffset(navOffsetDesktop);
    } else {
      setNavOffset(navOffsetMobile);
    }
  };

  /**
   * We observe the linkTotalWidth to trigger the handleOverflow method.
   * We also set an event listener to the window to manage the overflow
   * when the window is being resized. To make the resize listener as
   * smooth as possible we update a state prop (windowResized) so we can
   * trigger actions once the resize even has finished. This helps stop
   * event propagation. We also use a event trottle for the same reason.
   */
  useEffect(() => {
    if (linksTotalWidth) {
      handleOverflow();
      handleNavOffset();
      if (!hasResizeListener) {
        const eventListenerCallback = () => {
          setWindowResized(true);
        };

        trottle(window, 'resize', 50, eventListenerCallback);
      }
    }
  }, [linksTotalWidth]);

  /**
   * Here we are observing windowResized so that we can trigger the
   * handleOverflow methods. Once these are triggered we reset
   * windowResized.
   */
  useEffect(() => {
    if (windowResized) {
      handleOverflow();
      setHasResizeListener(true);
      setWindowResized(false);
      handleNavOffset();
    }
  }, [windowResized]);

  /**
   * Here we are recalculating the overall width of the links in view.
   * Each link has a margin-right of 24px that we also add into the
   * calulation.
   */
  useEffect(() => {
    if (linksContainer.current) {
      const linkMargin: number = 20;

      setLinksTotalWidth(
        calculateWidthOfChildren(linksContainer.current, linkMargin),
      );
    }
  }, [linksContainer, cutOffIndex]);

  /**
   * Handle dropdown toggle when clicking outside of filter.
   */
  useOutsideAlerter(moreLinksContainer, () => {
    setShowDropdown(false);
  });

  return (
    <section
      className={`
        page-header
        ${cutOffIndex && 'page-header--has-overflow'}
        ${heroImage && 'page-header--with-image'}
      `}
      aria-label={title}
    >
      {heroImage && (
        <div className="page-header__hero-image">
          <ImageObject image={heroImage} />
        </div>
      )}

      {link && (
        <div className="page-header__breadcrumbs">
          <Container>
            <a className="link link--has-icon" href={link.url}>
              <div className="link__icon">
                <SvgImage title="arrow" reference="#icon-think-arrow" />
              </div>
              <span>{link.title}</span>
            </a>
          </Container>
        </div>
      )}

      <Container>
        <div className="page-header__inner">
          {title && (
            <div className="page-header__title">
              <h1>{title}</h1>
            </div>
          )}

          <nav className="page-header__navigation">
            {links && (
              <div className="page-header__links">
                <ul ref={linksContainer} className="page-header__links__list">
                  {links.slice(0, cutOffIndex).map((item) => (
                    <li
                      key={uuidv4()}
                      data-url={item.url}
                      data-title={item.title}
                      className={`
                        ${item.hidden && 'hidden'}
                        page-header__links__item
                        page-header__links__item--${item?.discipline}
                      `}
                    >
                      <a
                        href={item.url}
                        className={`
                          page-header__links__link
                          ${item.isActive && 'active'}
                        `}
                      >
                        {item.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Boolean(cutOffIndex < links.length) && (
              <div ref={moreLinksContainer} className={`page-header__view-more ${showDropdown && 'active'}`}>
                <button onClick={() => setShowDropdown(!showDropdown)}>
                  <span>more</span>
                  <SvgImage title="toggle more view more" reference="#icon-plus" />
                </button>

                <div className={`page-header__filter ${!showDropdown && 'hidden'}`}>
                  <div className="page-header__filter__box-shadow" />
                  <PerfectScrollbar>
                    <div className="page-header__filter__inner">
                      <ul>
                        {links.slice(cutOffIndex, links.length).map((item) => (
                          <li key={uuidv4()}>
                            <a href={item.url} className={`${item.isActive && 'active'}`}>
                              {item.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </PerfectScrollbar>
                </div>
              </div>
            )}
          </nav>
        </div>
      </Container>
    </section>
  );
};

export default PageHeader;
